<script setup>
import { computed, ref, watch } from 'vue'
import { usePublicDate } from '@/composables/usePublicDate'

// A date input that says something when it is empty (2026-09-09).
//
// iOS renders a blank box for an empty `<input type="date">` — no placeholder,
// no hint — so the "Date" cell of the home planner looked broken on a phone.
// This keeps the platform's own picker (the native input sits on top at zero
// opacity, so a tap opens the iOS/Android wheel exactly as before) and draws
// the visible layer ourselves: a calendar glyph plus either the chosen date,
// formatted in the visitor's language, or the placeholder.
//
// Fall-through attrs land on the wrapper, so each page keeps passing its own
// class (.plan-date, .planner__input …) and the box looks unchanged.
const props = defineProps({
  modelValue: { type: String, default: '' },
  // "date" or "datetime-local" — a transfer/package pickup needs the hour too,
  // and iOS renders that one blank when empty just the same.
  type: { type: String, default: 'date' },
  name: { type: String, default: undefined },
  min: { type: String, default: undefined },
  max: { type: String, default: undefined },
  required: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
  ariaLabel: { type: String, default: undefined },
})
const emit = defineEmits([ 'update:modelValue' ])

const { formatDate, formatTime } = usePublicDate()
const native = ref(null)

// Mirror the value locally: the home planner uses this field uncontrolled
// (a plain GET form, name= and no v-model), and there the prop never changes,
// so the visible text has to follow the input itself.
const inner = ref(props.modelValue)
watch(() => props.modelValue, (v) => { inner.value = v })

const withTime = computed(() => props.type === 'datetime-local')

const display = computed(() => {
  if (!inner.value) return props.placeholder
  // Both shapes are bare local strings ("2026-10-20", "2026-10-20T14:30") —
  // no timezone, which is what the pickup contract expects (Phuket wall clock).
  const stamp = withTime.value ? inner.value : `${inner.value}T00:00:00`
  const day = formatDate(stamp, { day: 'numeric', month: 'short', year: 'numeric' })
  // hour: numeric, not 2-digit — "2:30 PM", not "02:30 PM"; the locale still
  // decides 12h vs 24h, so the text matches what the platform picker showed.
  return withTime.value ? `${day}, ${formatTime(stamp, { hour: 'numeric', minute: '2-digit' })}` : day
})

function onInput(event) {
  inner.value = event.target.value
  emit('update:modelValue', event.target.value)
}

// Desktop is not mobile here (2026-09-10). A phone opens the platform picker
// when the input itself is tapped, so hiding the input at opacity 0 costs
// nothing; Chromium on a PC only opens the calendar from the input's own
// indicator icon — which this field hides along with everything else. Clicking
// the box therefore focused an invisible input and looked completely dead.
// showPicker() is the supported way to open it from our own click. It throws
// when the browser refuses (no user gesture, already open) and is absent
// before Chrome/Edge 99, Firefox 101, Safari 16 — in both cases the platform's
// own tap-to-open behaviour is still there, so failing quietly is correct.
function openPicker() {
  const el = native.value
  if (!el || typeof el.showPicker !== 'function') return
  try { el.showPicker() } catch { /* not allowed, or already showing */ }
}
</script>

<template>
  <span class="datefield" :class="{ 'datefield--empty': !inner }">
    <svg class="datefield__glyph" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
    <span class="datefield__text">{{ display }}</span>
    <input
      ref="native"
      class="datefield__native"
      :type="type"
      :name="name"
      :min="min"
      :max="max"
      :required="required"
      :value="inner"
      :aria-label="ariaLabel || placeholder || undefined"
      @input="onInput"
      @change="onInput"
      @click="openPicker"
    >
  </span>
</template>

<style scoped>
.datefield {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 2.75rem; /* 44px: the phone tap target */
  box-sizing: border-box;
  cursor: pointer;
  color: var(--ink);
}
.datefield__glyph { flex: none; color: var(--muted); }
.datefield__text { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.datefield--empty .datefield__text { color: var(--muted); font-weight: 500; }
/* The real control: invisible, on top, full size — the platform picker still
   opens on tap, and the field stays keyboard- and form-native. */
.datefield__native {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: transparent;
  opacity: 0;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
}
.datefield:focus-within { outline: 2px solid color-mix(in oklch, var(--azure, var(--second)) 70%, transparent); outline-offset: 2px; border-radius: 0.4rem; }
</style>
